import { useState } from "react";
import { Button } from "components/atoms/Button";
import NumberInput from "components/atoms/NumberInput/NumberInput";

import { usePermissions } from "hooks/usePermissions";
import { UserPermissions } from "utils/enums";

import { useUpdateCustomerAccount } from "hooks/useUpdateCustomerAccount";

import styles from "./OrganizationInfo.module.scss";

interface OrganizationInfoProps {
    activeOrganization: CustomerAccount;
}

function safePct(value: number | undefined) {
    if (value) {
        return (value * 100.0).toFixed(2);
    }
    return value;
}

function safeDecimal(value: number | undefined) {
    if (value) {
        return (value / 100.0).toFixed(3);
    }
    return value;
}

function orgToDict(activeOrganization: CustomerAccount) {
    return {
        createdTimestamp: activeOrganization.createdTimestamp,
        currencyCode: activeOrganization.currencyCode,
        name: activeOrganization.name,
        customerAccountUid: activeOrganization.customerAccountUid,
        skipRooftopDetection: activeOrganization.skipRooftopDetection,
        tags: activeOrganization.tags,
        defaultDiscountRate: safePct(activeOrganization.defaultDiscountRate),
        defaultGridIntensityScheduleScenario: activeOrganization.defaultGridIntensityScheduleScenario,
        defaultWeightEmissionSavings: safePct(activeOrganization.defaultWeightEmissionSavings),
        defaultWeightNetPresentValue: safePct(activeOrganization.defaultWeightNetPresentValue),
        defaultUtilityInflationRate: safePct(activeOrganization.defaultUtilityInflationRate),
        exchangeRateOverride: activeOrganization.exchangeRateOverride,
        prohibitGenerativeAi: activeOrganization.prohibitGenerativeAi,
        defaultLandlordShareCost: safePct(activeOrganization.defaultLandlordShareCost),
        defaultLandlordShareElectricity: safePct(activeOrganization.defaultLandlordShareElectricity),
        defaultLandlordShareNaturalGas: safePct(activeOrganization.defaultLandlordShareNaturalGas),
        defaultLandlordShareSteam: safePct(activeOrganization.defaultLandlordShareSteam),
    };
}

const PERCENTAGE_FIELDS = [
    "defaultDiscountRate",
    "defaultWeightEmissionSavings",
    "defaultWeightNetPresentValue",
    "defaultUtilityInflationRate",
    "defaultLandlordShareCost",
    "defaultLandlordShareElectricity",
    "defaultLandlordShareNaturalGas",
    "defaultLandlordShareSteam",
];

function validateOrgField(fieldName: string, value: any): string | null {
    if (value === "" || value === undefined || value === null) return null;
    if (PERCENTAGE_FIELDS.includes(fieldName)) {
        const num = Number(value);
        if (Number.isNaN(num) || num < 0 || num > 100) return "Must be between 0 and 100";
    }
    return null;
}

function OrganizationInfo({ activeOrganization }: OrganizationInfoProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { hasPermission } = usePermissions();
    const [workingOrganization, setWorkingOrganization] = useState(orgToDict(activeOrganization));
    const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});

    const { mutate: updateCustomerAccount, isLoading: isUpdatingCustomerAccount } = useUpdateCustomerAccount(
        activeOrganization.customerAccountUid
    );

    function updateOrg(e: React.ChangeEvent<any>, fieldName: string) {
        const newValue = e.target.value;
        setWorkingOrganization((prevState) => {
            const u = structuredClone(prevState);
            (u as any)[fieldName] = newValue;
            return u;
        });
        const error = validateOrgField(fieldName, newValue);
        setValidationErrors((prev) => ({ ...prev, [fieldName]: error }));
    }

    function saveOrg() {
        const errors: Record<string, string | null> = {};
        PERCENTAGE_FIELDS.forEach((field) => {
            errors[field] = validateOrgField(field, (workingOrganization as any)[field]);
        });
        setValidationErrors(errors);
        if (Object.values(errors).some((e) => e !== null)) return;

        setIsSaving(true);
        const updateMe = structuredClone(workingOrganization);
        updateMe.defaultDiscountRate = safeDecimal(updateMe.defaultDiscountRate as number | undefined);
        updateMe.defaultWeightEmissionSavings = safeDecimal(
            updateMe.defaultWeightEmissionSavings as number | undefined
        );
        updateMe.defaultWeightNetPresentValue = safeDecimal(
            updateMe.defaultWeightNetPresentValue as number | undefined
        );
        updateMe.defaultUtilityInflationRate = safeDecimal(updateMe.defaultUtilityInflationRate as number | undefined);
        updateMe.defaultLandlordShareCost = safeDecimal(updateMe.defaultLandlordShareCost as number | undefined);
        updateMe.defaultLandlordShareElectricity = safeDecimal(
            updateMe.defaultLandlordShareElectricity as number | undefined
        );
        updateMe.defaultLandlordShareNaturalGas = safeDecimal(
            updateMe.defaultLandlordShareNaturalGas as number | undefined
        );
        updateMe.defaultLandlordShareSteam = safeDecimal(updateMe.defaultLandlordShareSteam as number | undefined);

        updateCustomerAccount(updateMe as CustomerAccount);
        setIsEditing(!isEditing);
        setIsSaving(false);
    }

    function cancelUpdate() {
        setWorkingOrganization(orgToDict(activeOrganization));
        setValidationErrors({});
        setIsEditing(!isEditing);
    }

    const hasValidationErrors = Object.values(validationErrors).some((e) => e !== null);

    const createdYear = workingOrganization.createdTimestamp!.split(" ")[0];
    return (
        <div>
            <h2 className={styles.bottomMargin}>Organization</h2>
            <div className={styles.organizationContainer}>
                <h1>{workingOrganization.name}</h1>
                <table>
                    <tr>
                        <td className={styles.fieldTitle}>Name</td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {workingOrganization.name}
                            </span>
                            <input
                                style={{
                                    display: isEditing ? "block" : "none",
                                }}
                                type="text"
                                value={workingOrganization.name}
                                onChange={(e) => updateOrg(e, "name")}
                                className="filter-input"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>Created</td>
                        <td className={styles.fieldValue}>
                            <span>{createdYear}</span>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>Currency</td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {workingOrganization.currencyCode}
                            </span>
                            <select
                                style={{
                                    display: isEditing ? "block" : "none",
                                    padding: "0",
                                }}
                                onChange={(e) => updateOrg(e, "currencyCode")}
                                className="filter-input"
                            >
                                <option selected={workingOrganization.currencyCode === "USD"} value="USD">
                                    USD
                                </option>
                                <option selected={workingOrganization.currencyCode === "CAD"} value="CAD">
                                    CAD
                                </option>
                            </select>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>Tags</td>
                        <td className={styles.fieldValue}>
                            <span>{workingOrganization.tags}</span>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>Default Discount Rate {isEditing ? "%" : ""}</td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {workingOrganization.defaultDiscountRate}%
                            </span>
                            <div
                                style={{
                                    display: isEditing ? "block" : "none",
                                }}
                            >
                                <NumberInput
                                    className="filter-input"
                                    value={workingOrganization.defaultDiscountRate}
                                    onChange={(e) => updateOrg(e, "defaultDiscountRate")}
                                />
                                {validationErrors.defaultDiscountRate && (
                                    <span className={styles.fieldError}>{validationErrors.defaultDiscountRate}</span>
                                )}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>Default Utility Inflation Rate {isEditing ? "%" : ""}</td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {workingOrganization.defaultUtilityInflationRate}%
                            </span>
                            <div
                                style={{
                                    display: isEditing ? "block" : "none",
                                }}
                            >
                                <NumberInput
                                    className="filter-input"
                                    value={workingOrganization.defaultUtilityInflationRate}
                                    onChange={(e) => updateOrg(e, "defaultUtilityInflationRate")}
                                />
                                {validationErrors.defaultUtilityInflationRate && (
                                    <span className={styles.fieldError}>
                                        {validationErrors.defaultUtilityInflationRate}
                                    </span>
                                )}
                            </div>
                        </td>
                    </tr>
                    <tr style={{ display: "none" }}>
                        <td className={styles.fieldTitle}>CAD to Local Exchange Rate Override</td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {workingOrganization.exchangeRateOverride}
                            </span>
                            <div
                                style={{
                                    display: isEditing ? "block" : "none",
                                }}
                            >
                                <NumberInput
                                    className="filter-input"
                                    value={workingOrganization.exchangeRateOverride}
                                    onChange={(e) => updateOrg(e, "exchangeRateOverride")}
                                />
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>
                            Recommendation Weight: Emission Savings {isEditing ? "%" : ""}
                        </td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {workingOrganization.defaultWeightEmissionSavings}%
                            </span>
                            <div
                                style={{
                                    display: isEditing ? "block" : "none",
                                }}
                            >
                                <NumberInput
                                    className="filter-input"
                                    value={workingOrganization.defaultWeightEmissionSavings}
                                    onChange={(e) => updateOrg(e, "defaultWeightEmissionSavings")}
                                />
                                {validationErrors.defaultWeightEmissionSavings && (
                                    <span className={styles.fieldError}>
                                        {validationErrors.defaultWeightEmissionSavings}
                                    </span>
                                )}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>
                            Recommendation Weight: Net Present Value {isEditing ? "%" : ""}
                        </td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {workingOrganization.defaultWeightNetPresentValue}%
                            </span>
                            <div
                                style={{
                                    display: isEditing ? "block" : "none",
                                }}
                            >
                                <NumberInput
                                    className="filter-input"
                                    value={workingOrganization.defaultWeightNetPresentValue}
                                    onChange={(e) => updateOrg(e, "defaultWeightNetPresentValue")}
                                />
                                {validationErrors.defaultWeightNetPresentValue && (
                                    <span className={styles.fieldError}>
                                        {validationErrors.defaultWeightNetPresentValue}
                                    </span>
                                )}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>Default Landlord Share: Cost {isEditing ? "%" : ""}</td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {workingOrganization.defaultLandlordShareCost}%
                            </span>
                            <div
                                style={{
                                    display: isEditing ? "block" : "none",
                                }}
                            >
                                <NumberInput
                                    className="filter-input"
                                    value={workingOrganization.defaultLandlordShareCost}
                                    onChange={(e) => updateOrg(e, "defaultLandlordShareCost")}
                                />
                                {validationErrors.defaultLandlordShareCost && (
                                    <span className={styles.fieldError}>
                                        {validationErrors.defaultLandlordShareCost}
                                    </span>
                                )}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>
                            Default Landlord Share: Electricity {isEditing ? "%" : ""}
                        </td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {workingOrganization.defaultLandlordShareElectricity}%
                            </span>
                            <div
                                style={{
                                    display: isEditing ? "block" : "none",
                                }}
                            >
                                <NumberInput
                                    className="filter-input"
                                    value={workingOrganization.defaultLandlordShareElectricity}
                                    onChange={(e) => updateOrg(e, "defaultLandlordShareElectricity")}
                                />
                                {validationErrors.defaultLandlordShareElectricity && (
                                    <span className={styles.fieldError}>
                                        {validationErrors.defaultLandlordShareElectricity}
                                    </span>
                                )}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>
                            Default Landlord Share: Natural Gas {isEditing ? "%" : ""}
                        </td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {workingOrganization.defaultLandlordShareNaturalGas}%
                            </span>
                            <div
                                style={{
                                    display: isEditing ? "block" : "none",
                                }}
                            >
                                <NumberInput
                                    className="filter-input"
                                    value={workingOrganization.defaultLandlordShareNaturalGas}
                                    onChange={(e) => updateOrg(e, "defaultLandlordShareNaturalGas")}
                                />
                                {validationErrors.defaultLandlordShareNaturalGas && (
                                    <span className={styles.fieldError}>
                                        {validationErrors.defaultLandlordShareNaturalGas}
                                    </span>
                                )}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>Default Landlord Share: Steam {isEditing ? "%" : ""} </td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {workingOrganization.defaultLandlordShareSteam}%
                            </span>
                            <div
                                style={{
                                    display: isEditing ? "block" : "none",
                                }}
                            >
                                <NumberInput
                                    className="filter-input"
                                    value={workingOrganization.defaultLandlordShareSteam}
                                    onChange={(e) => updateOrg(e, "defaultLandlordShareSteam")}
                                />
                                {validationErrors.defaultLandlordShareSteam && (
                                    <span className={styles.fieldError}>
                                        {validationErrors.defaultLandlordShareSteam}
                                    </span>
                                )}
                            </div>
                        </td>
                    </tr>
                    <tr style={{ display: "none" }}>
                        <td className={styles.fieldTitle}>Default Grid Intensity Schedule</td>
                        <td className={styles.fieldValue}>
                            <span>{workingOrganization.defaultGridIntensityScheduleScenario}</span>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>Prohibit Generative AI</td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {`${workingOrganization.prohibitGenerativeAi}`}
                            </span>
                            <select
                                style={{
                                    display: isEditing ? "block" : "none",
                                    padding: "0",
                                }}
                                onChange={(e) => updateOrg(e, "prohibitGenerativeAi")}
                                className="filter-input"
                            >
                                <option selected={workingOrganization.prohibitGenerativeAi === true} value="true">
                                    True
                                </option>
                                <option selected={workingOrganization.prohibitGenerativeAi === false} value="false">
                                    False
                                </option>
                            </select>
                        </td>
                    </tr>
                    <tr>
                        <td className={styles.fieldTitle}>Skip Rooftop Feature Detection</td>
                        <td className={styles.fieldValue}>
                            <span
                                style={{
                                    display: isEditing ? "none" : "block",
                                }}
                            >
                                {`${workingOrganization.skipRooftopDetection}`}
                            </span>
                            <select
                                style={{
                                    display: isEditing ? "block" : "none",
                                    padding: "0",
                                }}
                                onChange={(e) => updateOrg(e, "skipRooftopDetection")}
                                className="filter-input"
                            >
                                <option selected={workingOrganization.skipRooftopDetection === true} value="true">
                                    True
                                </option>
                                <option selected={workingOrganization.skipRooftopDetection === false} value="false">
                                    False
                                </option>
                            </select>
                        </td>
                    </tr>
                </table>
                {/* TODO: change to UpdateCustomerAccount */}
                {hasPermission(UserPermissions.UseInternalTools) && (
                    <div className={styles.buttonBar}>
                        <Button
                            type="primary"
                            id="edit-button"
                            disabled={isSaving || isUpdatingCustomerAccount}
                            onClick={() => setIsEditing(!isEditing)}
                            style={{ display: isEditing ? "none" : "block" }}
                        >
                            <div style={{ marginTop: "-5px" }}>Edit</div>
                        </Button>
                        <Button
                            id="save-button"
                            disabled={isSaving || isUpdatingCustomerAccount || hasValidationErrors}
                            onClick={() => saveOrg()}
                            type="primary"
                            style={{ display: isEditing ? "block" : "none" }}
                        >
                            <div style={{ marginTop: "-5px" }}>Save</div>
                        </Button>
                        <Button
                            id="cancel-button"
                            disabled={isSaving || isUpdatingCustomerAccount}
                            onClick={() => cancelUpdate()}
                            type="secondary"
                            style={{ display: isEditing ? "block" : "none" }}
                        >
                            <div style={{ marginTop: "-5px" }}>Cancel</div>
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default OrganizationInfo;
